import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ArrowLeft, ArrowRight, X, CheckCircle, Clock, FileText, Receipt } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

/* /assessor/hale-invoices — admin console for the Phase 1 lightweight
 * invoice workflow. The admin walks an invoice request through:
 *   requested → invoice_sent → paid → activated
 *                                ↘ cancelled (terminal)
 * The `activated` transition cascades to flipping the linked Hale
 * membership row to status=active, unlocking SKU intake on the
 * brand-side workspace.
 *
 * Intentionally bare-bones: no embedded billing automation, no
 * tax engine, no payouts. Pilot operations only.
 */

const STATUS_TONE = {
  requested:    { color: '#F59E0B', label: 'Awaiting invoice' },
  invoice_sent: { color: '#3B82F6', label: 'Invoice sent' },
  paid:         { color: '#10B981', label: 'Paid' },
  activated:    { color: '#10B981', label: 'Membership activated' },
  cancelled:    { color: '#9B1C1C', label: 'Cancelled' },
};

const NEXT_ACTIONS = {
  requested:    [{ key: 'invoice_sent', label: 'Mark invoice sent' },
                 { key: 'cancelled',    label: 'Cancel' }],
  invoice_sent: [{ key: 'paid',      label: 'Mark paid' },
                 { key: 'cancelled', label: 'Cancel' }],
  paid:         [{ key: 'activated', label: 'Activate membership' }],
  activated:    [],
  cancelled:    [],
};

const METHOD_LABEL = {
  ach: 'ACH',
  wire: 'Wire',
  bill_com: 'bill.com',
  check: 'Check',
};

export default function HaleInvoicesPage() {
  const [items, setItems] = useState([]);
  const [counters, setCounters] = useState({});
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/hale-marketplace/admin/invoice-requests`, {
        params: status ? { status } : {},
        withCredentials: true,
      });
      setItems(r.data.items || []);
      setCounters(r.data.counters || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load invoice requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#0A0A0A' }}>
      <header className="border-b px-6 lg:px-12 py-5" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-[#E63946]">
              Phase 1 · invoiced activation queue
            </p>
            <h1 className="text-2xl font-light mt-1"
                style={{ fontFamily: '"Cormorant Garamond", "Times New Roman", serif' }}>
              HALE invoice requests
            </h1>
          </div>
          <Link to="/assessor" data-testid="back-to-assessor"
                className="text-[10px] uppercase tracking-[0.22em] text-white/50 hover:text-white">
            <ArrowLeft size={12} className="inline mr-1" /> Assessor console
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        {/* Counters */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8" data-testid="counters">
          {['requested', 'invoice_sent', 'paid', 'activated', 'cancelled', 'total'].map((k) => {
            const isActive = (status === '' && k === 'total') || status === k;
            const label = k === 'total' ? 'All' : STATUS_TONE[k]?.label || k;
            return (
              <button
                key={k}
                data-testid={`filter-${k}`}
                onClick={() => setStatus(k === 'total' ? '' : k)}
                className={`text-left rounded-lg p-4 border transition-colors ${
                  isActive
                    ? 'border-[#E63946] bg-[#E63946]/5'
                    : 'border-white/10 bg-white/[0.02] hover:border-white/30'
                }`}
              >
                <p className="text-[10px] uppercase tracking-[0.22em] text-white/40">{label}</p>
                <p className="text-2xl font-bold mt-1 tabular-nums">{counters[k] ?? 0}</p>
              </button>
            );
          })}
        </div>

        {loading ? (
          <p className="text-white/40 text-sm">Loading…</p>
        ) : items.length === 0 ? (
          <div className="rounded-lg p-12 text-center"
               style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.08)' }}
               data-testid="empty-invoices">
            <Receipt size={32} className="mx-auto text-white/30" />
            <p className="mt-4 text-base text-white/70">No invoice requests in this view.</p>
          </div>
        ) : (
          <div className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
            <table className="w-full text-sm" data-testid="invoice-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-white/40"
                    style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                  <th className="px-5 py-3 font-bold">Brand</th>
                  <th className="px-5 py-3 font-bold">Tier · Track</th>
                  <th className="px-5 py-3 font-bold">Billing entity</th>
                  <th className="px-5 py-3 font-bold">Method</th>
                  <th className="px-5 py-3 font-bold">Status</th>
                  <th className="px-5 py-3 font-bold">Submitted</th>
                  <th className="px-5 py-3 font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((inv) => {
                  const tone = STATUS_TONE[inv.status] || STATUS_TONE.requested;
                  return (
                    <tr key={inv.id} className="border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                        data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-5 py-4">
                        <button onClick={() => setSelected(inv)} className="text-white hover:underline text-left font-medium">
                          {inv.brand_name || '—'}
                        </button>
                        <p className="text-[11px] text-white/40 mt-0.5">{inv.ap_contact_email}</p>
                      </td>
                      <td className="px-5 py-4 text-white/70">
                        {inv.tier} · {inv.track}
                      </td>
                      <td className="px-5 py-4 text-white/70">{inv.billing_entity}</td>
                      <td className="px-5 py-4 text-white/70">{METHOD_LABEL[inv.payment_method] || inv.payment_method}</td>
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em]"
                              style={{ color: tone.color }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: tone.color }} />
                          {tone.label}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-white/50 text-xs">
                        {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <Button
                          data-testid={`open-invoice-${inv.id}`}
                          onClick={() => setSelected(inv)}
                          size="sm"
                          variant="outline"
                          className="rounded-full bg-transparent border-white/15 text-white/80 hover:border-[#E63946] hover:text-[#E63946] text-[10px] uppercase tracking-[0.18em] font-bold h-8 px-4"
                        >
                          Open <ArrowRight size={11} className="ml-1.5" weight="bold" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {selected && (
          <InvoiceDrawer
            invoice={selected}
            onClose={() => setSelected(null)}
            onUpdated={async (fresh) => { setSelected(fresh); await reload(); }}
          />
        )}
      </main>
    </div>
  );
}

function InvoiceDrawer({ invoice, onClose, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [ref, setRef] = useState('');
  const actions = NEXT_ACTIONS[invoice.status] || [];

  const apply = async (action) => {
    setBusy(true);
    try {
      const r = await axios.patch(
        `${API}/api/hale-marketplace/admin/invoice-requests/${invoice.id}`,
        { action, note: note || null, invoice_reference: ref || null },
        { withCredentials: true },
      );
      toast.success(`Updated to ${action.replace('_', ' ')}`);
      await onUpdated(r.data);
      setNote(''); setRef('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose} data-testid="invoice-drawer">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" />
      <div className="w-full max-w-xl bg-[#0A0A0A] border-l overflow-y-auto"
           style={{ borderColor: 'rgba(255,255,255,0.06)' }}
           onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between px-7 py-5 border-b sticky top-0 bg-[#0A0A0A]"
                style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <div>
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#E63946]">Invoice request</p>
            <h3 className="text-xl text-white mt-1">{invoice.brand_name}</h3>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="close-invoice-drawer">
            <X size={20} />
          </button>
        </header>

        <div className="px-7 py-7 space-y-6">
          <Stat label="Billing entity" value={invoice.billing_entity} />
          <Stat label="AP contact" value={`${invoice.ap_contact_name} · ${invoice.ap_contact_email}`} />
          <Stat label="Tier · Track" value={`${invoice.tier} · ${invoice.track}`} />
          <Stat label="Payment method" value={METHOD_LABEL[invoice.payment_method] || invoice.payment_method} />
          {invoice.billing_address && <Stat label="Billing address" value={invoice.billing_address} />}
          {invoice.purchase_order_required && (
            <p className="text-[12px] font-bold uppercase tracking-[0.22em] text-[#F59E0B]">PO required before invoice</p>
          )}
          {invoice.notes && <Stat label="Brand notes" value={invoice.notes} />}
          {invoice.invoice_reference && <Stat label="Invoice ref" value={invoice.invoice_reference} />}

          {/* Action panel */}
          {actions.length > 0 && (
            <div className="pt-5 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/40 mb-3">
                Walk through the workflow
              </p>
              <div className="space-y-3">
                <input
                  data-testid="invoice-action-ref"
                  value={ref} onChange={(e) => setRef(e.target.value)}
                  placeholder="Invoice reference (optional)"
                  className="w-full bg-transparent border-b text-sm text-white py-2 focus:outline-none focus:border-[#E63946]"
                  style={{ borderColor: 'rgba(255,255,255,0.08)' }}
                />
                <textarea
                  data-testid="invoice-action-note"
                  value={note} onChange={(e) => setNote(e.target.value)}
                  rows={2} placeholder="Note (optional)"
                  className="w-full bg-transparent border-b text-sm text-white py-2 focus:outline-none focus:border-[#E63946]"
                  style={{ borderColor: 'rgba(255,255,255,0.08)' }}
                />
                <div className="flex flex-wrap gap-2 mt-2">
                  {actions.map((a) => (
                    <Button
                      key={a.key}
                      data-testid={`invoice-do-${a.key}`}
                      onClick={() => apply(a.key)}
                      disabled={busy}
                      className={`rounded-full h-10 px-5 text-[10px] uppercase tracking-[0.22em] font-bold disabled:opacity-50 ${
                        a.key === 'activated'
                          ? 'bg-[#E63946] text-black hover:bg-[#C9192C]'
                          : a.key === 'cancelled'
                            ? 'bg-transparent border border-white/15 text-white/60 hover:text-[#9B1C1C] hover:border-[#9B1C1C]'
                            : 'bg-white/5 text-white border border-white/20 hover:bg-white/10'
                      }`}
                    >
                      {a.key === 'activated' && <CheckCircle size={12} weight="fill" className="mr-1.5" />}
                      {a.key === 'invoice_sent' && <FileText size={12} weight="fill" className="mr-1.5" />}
                      {a.key === 'paid' && <Receipt size={12} weight="fill" className="mr-1.5" />}
                      {a.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Audit trail */}
          {invoice.audit && invoice.audit.length > 0 && (
            <div className="pt-5 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/40 mb-3">Audit trail</p>
              <ol className="space-y-2">
                {invoice.audit.map((a, i) => (
                  <li key={i} className="text-xs text-white/65 flex items-start gap-2">
                    <Clock size={11} className="mt-1 flex-shrink-0 text-white/40" />
                    <div>
                      <span className="text-white">{a.action}</span>
                      {a.invoice_reference && <span className="text-white/50"> · {a.invoice_reference}</span>}
                      {a.note && <span className="text-white/50"> — {a.note}</span>}
                      <p className="text-white/30">{a.by} · {new Date(a.at).toLocaleString()}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/40 mb-1">{label}</p>
      <p className="text-sm text-white/85">{value || '—'}</p>
    </div>
  );
}
